import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import apiClient from "../../lib/utils/network-client";
import useToast from "../../hooks/useToast";
import { emptyRecordFor, FIELD_CONFIG, MSR_DATASETS } from "./msrConfig";

const MsrRecordDialog = ({ dataset, record, open, onOpenChange, onSaved }) => {
  const config = MSR_DATASETS[dataset];
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(() => emptyRecordFor(dataset));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initial = emptyRecordFor(dataset);
    if (record) {
      [...config.fields, "status"].forEach((field) => {
        initial[field] = record[field] ?? "";
      });
    }
    setForm(initial);
  }, [config.fields, dataset, open, record]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      if (record) {
        await apiClient.put(`/msr/records/${dataset}/${record.id}`, form);
      } else {
        await apiClient.post(`/msr/records/${dataset}`, form);
      }
      showSuccessToast(`${config.label} ${record ? "updated" : "created"} successfully`);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      showErrorToast(err.response?.data?.message || `Unable to save ${config.singular}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{record ? "Edit" : "Add"} {config.singular}</DialogTitle>
          <DialogDescription>
            Changes are reflected on the public MSR page when this record is published.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            {config.fields.map((field) => {
              const fieldConfig = FIELD_CONFIG[field];
              const Component = fieldConfig.multiline ? Textarea : Input;
              return (
                <label key={field} className={`space-y-2 ${fieldConfig.multiline ? "md:col-span-2" : ""}`}>
                  <span className="text-sm font-semibold text-slate-700">{fieldConfig.label}</span>
                  <Component
                    type={fieldConfig.type || "text"}
                    min={fieldConfig.type === "number" ? 0 : undefined}
                    value={form[field] ?? ""}
                    onChange={(event) => updateField(field, event.target.value)}
                    className="rounded-lg"
                    required={["name", "designation", "department", "employee_id", "seat_count"].includes(field)}
                  />
                </label>
              );
            })}

            <label className="space-y-2">
              <span className="text-sm font-semibold text-slate-700">Status</span>
              <Select value={form.status} onValueChange={(value) => updateField("status", value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Record"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MsrRecordDialog;
